import { Eye, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useEndImpersonation } from "@/hooks/useImpersonation";
import { useAuthSession } from "@/lib/session-context";

/**
 * The "Viewing as" bar while a super admin sees the app as another user:
 * pinned across the very top of every page, above the sidebar and topbar.
 * Names the user and their role, says the view is read-only (the server
 * refuses every save), and carries the way back to the super admin's own
 * session.
 *
 * Its height (`h-8`) is `VIEWING_AS_BAR_HEIGHT_PX` in `lib/top-chrome.ts`,
 * which everything pinned below it offsets by. Change one, change both.
 */
export function ImpersonationBanner() {
  const { impersonating, display_name, role_label } = useAuthSession();
  const end = useEndImpersonation();
  if (!impersonating) return null;
  return (
    // The tint is translucent, so it sits on the topbar's opaque surface:
    // the page scrolls underneath.
    <div role="status" className="sticky top-0 z-40 h-8 shrink-0 bg-background-deep">
      <div className="flex h-full items-center gap-3 border-b border-primary-mid bg-primary-dim px-4 text-xs text-primary">
        <Eye size={13} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          Viewing as <span className="font-semibold">{display_name}</span>
          {role_label && <> · {role_label}</>} · Read-only
        </span>
        <Button
          size="sm"
          onClick={() =>
            end.mutate(undefined, {
              onError: () => toast.error("Couldn't return to your account."),
            })
          }
          disabled={end.isPending}
          className="h-6 shrink-0 rounded-full px-2.5 font-semibold"
        >
          <X size={13} strokeWidth={2.5} />
          {end.isPending ? "Exiting…" : "Exit view"}
        </Button>
      </div>
    </div>
  );
}
